import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

class BottomAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.closeButton = this.querySelector('[data-bottom-announcement-close]');
    this.isDesignMode = document.documentElement.classList.contains('shopify-design-mode');
    this.dismissible = this.dataset.dismissible === 'true';

    this.clearLegacyPersistentDismissal();

    if (this.dismissible && !this.isDesignMode && this.isDismissed()) {
      this.hide();
      return;
    }

    this.show();
    this.closeButton?.addEventListener('click', this.handleClose);
    window.addEventListener('resize', this.updateBodyOffset);
  }

  disconnectedCallback() {
    this.closeButton?.removeEventListener('click', this.handleClose);
    window.removeEventListener('resize', this.updateBodyOffset);
    this.clearBodyOffset();
  }

  get storageKey() {
    return this.dataset.storageKey || 'outfitworksBottomAnnouncementDismissed';
  }

  isDismissed() {
    try {
      return window.sessionStorage.getItem(this.storageKey) === 'true';
    } catch (_error) {
      return false;
    }
  }

  clearLegacyPersistentDismissal() {
    try {
      const legacyKeys = new Set([this.storageKey, 'outfitworksBottomAnnouncementDismissed']);

      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);

        if (key?.startsWith('outfitworksBottomAnnouncementDismissed')) {
          legacyKeys.add(key);
        }
      }

      legacyKeys.forEach((key) => window.localStorage.removeItem(key));
    } catch (_error) {
      // Ignore storage access errors; sessionStorage dismissal still works when available.
    }
  }

  show() {
    this.hidden = false;
    document.body.classList.add('has-bottom-announcement');
    this.updateBodyOffset();
  }

  hide() {
    this.hidden = true;
    this.clearBodyOffset();
  }

  clearBodyOffset() {
    document.body.classList.remove('has-bottom-announcement');
    document.body.style.removeProperty('--outfitworks-bottom-announcement-height');
  }

  handleClose = () => {
    if (this.dismissible && !this.isDesignMode) {
      try {
        window.sessionStorage.setItem(this.storageKey, 'true');
      } catch (_error) {
        // Customers can still dismiss the bar for this page view if storage is unavailable.
      }
    }

    this.hide();
  };

  updateBodyOffset = () => {
    if (this.hidden) return;

    window.requestAnimationFrame(() => {
      document.body.style.setProperty('--outfitworks-bottom-announcement-height', `${this.offsetHeight}px`);
    });
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

if (!customElements.get('bottom-announcement-bar')) {
  customElements.define('bottom-announcement-bar', BottomAnnouncementBar);
}
